import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ChevronLeft, ChevronRight, RotateCcw, Loader2 } from 'lucide-react';
import WizardResult from '@/components/wizard/WizardResult';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WizardFlow() {
  const [step, setStep] = useState(1);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resultLoading, setResultLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Selections
  const [ageGroup, setAgeGroup] = useState('');
  const [problems, setProblems] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [timeObj, setTimeObj] = useState('');

  useEffect(() => {
    axios.get(`${API}/wizard/config`).then(r => { setConfig(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const progress = result ? 100 : (step / 4) * 100;

  const canNext = () => {
    if (step === 1) return !!ageGroup;
    if (step === 2) return problems.length > 0;
    if (step === 3) return true;
    if (step === 4) return !!timeObj;
    return false;
  };

  const toggleProblem = (id) => {
    setProblems(prev => {
      if (prev.includes(id)) return prev.filter(p => p !== id);
      if (prev.length >= 3) return prev;
      return [...prev, id];
    });
  };

  const toggleCondition = (id) => {
    if (id === 'none') { setConditions(['none']); return; }
    setConditions(prev => {
      const filtered = prev.filter(c => c !== 'none');
      return filtered.includes(id) ? filtered.filter(c => c !== id) : [...filtered, id];
    });
  };

  const getFilteredProblems = () => {
    if (!config) return [];
    const ageCfg = config.age_groups.find(a => a.id === ageGroup);
    const excluded = ageCfg?.excluded_areas || [];
    return config.problems.filter(p => !excluded.includes(p.area_slug));
  };

  const handleSubmit = async () => {
    setResultLoading(true);
    try {
      const resp = await axios.post(`${API}/wizard/result`, {
        age_group: ageGroup,
        selected_problems: problems,
        special_conditions: conditions.filter(c => c !== 'none'),
        time_objective: timeObj,
      });
      setResult(resp.data);
      // Save session silently
      const token = localStorage.getItem('biounica_token');
      if (token) {
        axios.post(`${API}/wizard/save-session`, {
          age_group: ageGroup, selected_problems: problems,
          special_conditions: conditions.filter(c => c !== 'none'), time_objective: timeObj,
        }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
      }
    } catch { /* ignore */ }
    setResultLoading(false);
  };

  const reset = () => {
    setStep(1); setAgeGroup(''); setProblems([]); setConditions([]); setTimeObj(''); setResult(null);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#12100c]" /></div>;
  if (!config) return null;

  if (result) return <WizardResult result={result} onReset={reset} />;

  return (
    <div data-testid="wizard-flow">
      {/* Progress */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-[#4a4238]">Step {step} di 4</span>
          <span className="text-xs text-[#4a4238]">{Math.round(progress)}%</span>
        </div>
        <Progress value={progress} className="h-2 bg-gray-100 [&>div]:bg-[#c6a15b]" data-testid="wizard-progress" />
      </div>

      <Card className="rounded-2xl border-gray-100 shadow-sm">
        <CardContent className="p-5 sm:p-8">
          {/* STEP 1 */}
          {step === 1 && (
            <div data-testid="wizard-step-1">
              <h2 className="font-heading text-xl font-semibold text-[#12100c] mb-1">Chi è il tuo paziente o cliente?</h2>
              <p className="text-sm text-[#4a4238] mb-5">Seleziona la fascia che meglio lo descrive</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {config.age_groups.map(ag => (
                  <SelectCard key={ag.id} selected={ageGroup === ag.id} onClick={() => setAgeGroup(ag.id)}
                    icon={ag.icon} label={ag.label} sublabel={ag.sublabel} testId={`age-${ag.id}`} />
                ))}
              </div>
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div data-testid="wizard-step-2">
              <h2 className="font-heading text-xl font-semibold text-[#12100c] mb-1">Qual è il problema o l'obiettivo principale?</h2>
              <p className="text-sm text-[#4a4238] mb-1">Puoi selezionare fino a 3 opzioni</p>
              <Badge className="bg-[#c6a15b]/10 text-[#8a6620] border-none text-xs mb-5">{problems.length}/3 selezionate</Badge>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {getFilteredProblems().map(p => (
                  <SelectCard key={p.id} selected={problems.includes(p.id)} onClick={() => toggleProblem(p.id)}
                    icon={p.icon} label={p.label} testId={`problem-${p.id}`}
                    disabled={!problems.includes(p.id) && problems.length >= 3} />
                ))}
              </div>
            </div>
          )}

          {/* STEP 3 */}
          {step === 3 && (
            <div data-testid="wizard-step-3">
              <h2 className="font-heading text-xl font-semibold text-[#12100c] mb-1">Ci sono condizioni da tenere in considerazione?</h2>
              <p className="text-sm text-[#4a4238] mb-5">Seleziona tutto ciò che si applica (opzionale)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {config.conditions.map(c => (
                  <SelectCard key={c.id} selected={conditions.includes(c.id)} onClick={() => toggleCondition(c.id)}
                    icon={c.icon} label={c.label} testId={`condition-${c.id}`} />
                ))}
              </div>
            </div>
          )}

          {/* STEP 4 */}
          {step === 4 && (
            <div data-testid="wizard-step-4">
              <h2 className="font-heading text-xl font-semibold text-[#12100c] mb-1">In che ottica vuoi usare UMIFUL?</h2>
              <p className="text-sm text-[#4a4238] mb-5">Serve a inquadrare il modo d'uso più diffuso, non a stabilire una posologia</p>
              <div className="grid grid-cols-1 gap-3">
                {config.time_objectives.map(t => (
                  <SelectCard key={t.id} selected={timeObj === t.id} onClick={() => setTimeObj(t.id)}
                    icon={t.icon} label={t.label} sublabel={t.sublabel} testId={`time-${t.id}`} />
                ))}
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-5 border-t border-gray-100">
            <Button variant="ghost" onClick={() => step > 1 ? setStep(step - 1) : null}
              disabled={step === 1} className="text-[#4a4238] hover:text-[#12100c]" data-testid="wizard-back-btn">
              <ChevronLeft className="w-4 h-4 mr-1" /> Indietro
            </Button>
            {step < 4 ? (
              <Button onClick={() => setStep(step + 1)} disabled={!canNext()}
                className="bg-[#12100c] hover:bg-[#5f7d52] text-white rounded-full px-6" data-testid="wizard-next-btn">
                Avanti <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={!canNext() || resultLoading}
                className="bg-[#c6a15b] hover:bg-[#a07c34] text-[#12100c] rounded-full px-6" data-testid="wizard-submit-btn">
                {resultLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Vedi dove si colloca
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SelectCard({ selected, onClick, icon, label, sublabel, testId, disabled }) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      className={`flex items-center gap-3 p-3.5 rounded-xl border-2 text-left transition-all duration-200 w-full
        ${selected ? 'border-[#12100c] bg-[#12100c]/[0.03]' : 'border-gray-200 hover:border-[#5f7d52]/40 bg-white'}
        ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
      `}
      data-testid={testId}
    >
      <span className="text-xl shrink-0">{icon}</span>
      <div className="min-w-0">
        <p className={`text-sm font-medium leading-tight ${selected ? 'text-[#12100c]' : 'text-[#12100c]'}`}>{label}</p>
        {sublabel && <p className="text-xs text-[#4a4238] mt-0.5">{sublabel}</p>}
      </div>
      {selected && (
        <div className="ml-auto shrink-0 w-5 h-5 rounded-full bg-[#12100c] flex items-center justify-center">
          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
      )}
    </button>
  );
}
